import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';

export default function FarmSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    farmName: '',
    tagline: '',
    aboutText: '',
    brandColors: { primary: '#16a34a', secondary: '#15803d', accent: '#f59e0b' },
    logoUrl: '',
    heroImageUrl: '',
    teamMembers: [],
    socialLinks: {},
    storeSettings: {}
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/farm/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      alert('Failed to load farm settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/farm/settings`, settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (colorKey, value) => {
    setSettings(prev => ({
      ...prev,
      brandColors: { ...prev.brandColors, [colorKey]: value }
    }));
  };

  if (loading) {
    return <div className="text-center py-8">Loading farm settings...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Farm Store Settings</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 mb-2">Farm Name</label>
              <input
                type="text"
                value={settings.farmName}
                onChange={(e) => handleChange('farmName', e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Tagline</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                placeholder="Fresh from our farm to your table"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">About Us</label>
              <textarea
                value={settings.aboutText}
                onChange={(e) => handleChange('aboutText', e.target.value)}
                rows="6"
                className="w-full border rounded-md px-3 py-2"
                placeholder="Tell customers your farm story..."
              />
            </div>
          </div>
        </div>
        
        {/* Brand Colors */}
        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold mb-4">Brand Colors</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Primary</label>
              <input
                type="color"
                value={settings.brandColors.primary}
                onChange={(e) => handleColorChange('primary', e.target.value)}
                className="w-full h-10 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Secondary</label>
              <input
                type="color"
                value={settings.brandColors.secondary}
                onChange={(e) => handleColorChange('secondary', e.target.value)}
                className="w-full h-10 border rounded"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Accent</label>
              <input
                type="color"
                value={settings.brandColors.accent}
                onChange={(e) => handleColorChange('accent', e.target.value)}
                className="w-full h-10 border rounded"
              />
            </div>
          </div>
        </div>
        
        {/* Save Button */}
        <div className="border-t pt-6">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

